import { CSSProperties, Fragment, useRef } from 'react'
import cx from 'classnames'
import { ButtonBase, Paper } from '@mui/material'
import { dom } from '@pogokid/ui'
import { ProcessedEvent } from '../../types'
import { EventItemContent } from './EventItemContent'
import { EventItemMultiDayContent } from './EventItemMultiDayContent'
import { EventItemClickEvent } from '../../context/SchedulerProviderProps'
import {
  useSchedulerDispatchContext,
  useSchedulerPropsContext,
} from '../../context/schedulerContext'
import { setDraggedEvent } from '../../helpers/eventDrag'
import styles from './EventItem.module.css'

interface EventItemProps {
  event: ProcessedEvent
  isBackgroundEvent?: boolean
  isSelected?: boolean
  multiday?: boolean
  hasPrev?: boolean
  hasNext?: boolean
  showdate?: boolean
}

/**
 * This is the event displayed on the calendar
 * table grid
 */
export const EventItem = ({
  event,
  isBackgroundEvent,
  isSelected,
  multiday = false,
  hasPrev,
  hasNext,
  showdate = false,
}: EventItemProps) => {
  const { onEventItemClick } = useSchedulerPropsContext()
  const { showEventDetail } = useSchedulerDispatchContext()
  const eventBoxRef = useRef<HTMLDivElement>(null)

  return (
    <Fragment>
      <Paper
        ref={eventBoxRef}
        data-event-id={
          event.recurrenceId?.toISOString() ?? event.uid
        }
        data-calendar-id={event.calendarId}
        className={cx(styles.EventItem, {
          [styles.isDisabled]: event.disabled,
          [styles.isBackgroundEvent]: isBackgroundEvent,
          [styles.isSelected]: isSelected,
          [styles.isOnCalendar]: !!event.calendarId,
        })}
        style={
          {
            '--EventItem-colour': `var(--Scheduler-calendar-${event.calendarId}-colour)`,
          } as CSSProperties
        }
      >
        <ButtonBase
          className={styles.EventItem__button}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            if (onEventItemClick) {
              const eventItemClickEvent: EventItemClickEvent =
                new CustomEvent('eventItemClicked', {
                  detail: {
                    processedEvent: event,
                  },
                  bubbles: false,
                  composed: false,
                  cancelable: true,
                })
              onEventItemClick(eventItemClickEvent)
              if (!eventItemClickEvent.defaultPrevented) {
                showEventDetail(event.uid)
              }
            } else {
              showEventDetail(event.uid)
            }
          }}
          disabled={event.disabled}
        >
          <div
            style={{ height: '100%' }}
            draggable
            onDragStart={(e) => {
              const yOffset = dom.offsetRelativeToParent(
                e.clientY,
                e.currentTarget
              )
              e.stopPropagation()
              // The browser's drag image is a snapshot of the
              // draggable element. That element has no background
              // or border (they sit on the outer event box), so
              // without this only the text follows the pointer.
              // Use the whole event box, held where it was grabbed.
              const eventBox = eventBoxRef.current
              if (eventBox) {
                const rect = eventBox.getBoundingClientRect()
                e.dataTransfer.setDragImage(
                  eventBox,
                  e.clientX - rect.left,
                  e.clientY - rect.top
                )
              }
              e.dataTransfer.setData(
                'text/plain',
                JSON.stringify({ eventId: event.uid, yOffset })
              )
              setDraggedEvent({ eventId: event.uid, yOffset })
            }}
            onDragEnd={() => {
              setDraggedEvent(null)
            }}
            onDragOver={(e) => {
              e.stopPropagation()
              e.preventDefault()
            }}
            onDragEnter={(e) => {
              e.stopPropagation()
              e.preventDefault()
            }}
          >
            {multiday ? (
              <EventItemMultiDayContent
                event={event}
                hasNext={hasNext}
                hasPrev={hasPrev}
                showdate={showdate}
              />
            ) : (
              <EventItemContent
                event={event}
                showdate={showdate}
                isBackgroundEvent={isBackgroundEvent}
              />
            )}
          </div>
          <div className={styles.EventItem__dragEnd}></div>
        </ButtonBase>
      </Paper>
    </Fragment>
  )
}
